import { Option, Schema, SchemaGetter } from "effect";
import { WorkspaceId } from "../development-identifiers.ts";
import { WorkerspaceState } from "../models.ts";

// These codecs consume already URI-decoded text. The route owns exactly one
// percent-decoding pass, including static segments and malformed-path errors.
export const HistoryWorkspaceIdFromText = /* @__PURE__ */ (() => Schema.String.pipe(
  Schema.decodeTo(WorkspaceId, {
    decode: SchemaGetter.transform(value => value.toLowerCase()),
    encode: SchemaGetter.transform(value => value)
  })
))();

export const HistoryRevisionId = /* @__PURE__ */ (() => Schema.Int.check(
  Schema.isGreaterThan(0)
).pipe(Schema.brand("HistoryRevisionId")))();
export type HistoryRevisionId = typeof HistoryRevisionId.Type;

// Number() is the historical wire normalization, accepting e.g. 0x10 and 1e1.
export const HistoryRevisionIdFromText = /* @__PURE__ */ (() => Schema.String.pipe(
  Schema.decodeTo(HistoryRevisionId, {
    decode: SchemaGetter.transform(value => Number(value)),
    encode: SchemaGetter.transform(value => String(value))
  })
))();

export const HistoryListLimitFromQuery = /* @__PURE__ */ (() => Schema.NullOr(Schema.String).pipe(
  Schema.decodeTo(Schema.Finite, {
    decode: SchemaGetter.transform(value => Option.getOrElse(
      Schema.decodeUnknownOption(Schema.Finite)(Number(value ?? "50")), () => 50
    )),
    encode: SchemaGetter.transform(value => String(value))
  })
))();

// The router exposes repeated query keys as arrays. The compatibility handler
// uses URLSearchParams.get, so the first value remains authoritative.
export const WorkspaceHistoryListQuery = /* @__PURE__ */ (() => {
  const text = Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)]));
  return Schema.Struct({ path: text, limit: text });
})();

export const HistoryExpectedUpdatedAt = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)))();

// Paths deliberately retain the history API's vocabulary. Selection and source
// policy remain with the workspace owner; this boundary preserves duplicates.
export const HistoryRestorePaths = /* @__PURE__ */ (() => Schema.Array(
  Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512))
).check(Schema.isMinLength(1), Schema.isMaxLength(50)))();

const RestoreBodyShape = /* @__PURE__ */ (() => Schema.Struct({
  paths: Schema.optionalKey(HistoryRestorePaths),
  expectedUpdatedAt: Schema.optionalKey(HistoryExpectedUpdatedAt)
}))();
const RestoreField = /* @__PURE__ */ (() => Schema.Literals(["paths", "expectedUpdatedAt"]))();
export const unknownHistoryRestoreField = (body: Readonly<Record<string, unknown>>) =>
  Object.keys(body).find(key => !Schema.is(RestoreField)(key));

// Retain the old unknown-key ordering, including its accepted empty first key.
// Decode to the service shape so ignored keys never reach the durable owner.
export const WorkspaceRevisionRestoreBody = /* @__PURE__ */ (() => Schema.StructWithRest(
  RestoreBodyShape, [Schema.Record(Schema.String, Schema.Unknown)])
  .check(Schema.makeFilter(body => !unknownHistoryRestoreField(body)))
  .pipe(Schema.decodeTo(RestoreBodyShape, {
    decode: SchemaGetter.transform(value => value), encode: SchemaGetter.transform(value => value)
  })))();
export type WorkspaceRevisionRestoreBody = typeof WorkspaceRevisionRestoreBody.Type;

export const WorkspaceRevisionRestoreBodyFromText = /* @__PURE__ */ (() => Schema.String.pipe(
  Schema.decodeTo(Schema.fromJsonString(WorkspaceRevisionRestoreBody), {
    decode: SchemaGetter.transform(value => value.trim() ? value : "{}"),
    encode: SchemaGetter.transform(value => value)
  })
))();

export const WorkspaceHistoryRevision = /* @__PURE__ */ (() => Schema.Struct({
  id: HistoryRevisionId,
  createdAt: Schema.Number,
  source: Schema.String,
  summary: Schema.String,
  changedPaths: Schema.Array(Schema.String),
  fileCount: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
}))();
export type WorkspaceHistoryRevision = typeof WorkspaceHistoryRevision.Type;

export const WorkspaceHistorySource = /* @__PURE__ */ (() => Schema.Struct({
  ...WorkerspaceState.fields,
  createdAt: Schema.optionalKey(Schema.Number),
  updatedAt: Schema.optionalKey(Schema.Number)
}))();

export const WorkspaceHistorySnapshot = /* @__PURE__ */ (() => Schema.Struct({
  ...WorkspaceHistoryRevision.fields,
  state: WorkspaceHistorySource
}))();
export type WorkspaceHistorySnapshot = typeof WorkspaceHistorySnapshot.Type;

export const WorkspaceHistoryListResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: WorkspaceId,
  revisions: Schema.Array(WorkspaceHistoryRevision)
}))();

export const WorkspaceHistoryReadResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: WorkspaceId,
  revision: WorkspaceHistorySnapshot
}))();

export const WorkspaceHistoryRestoreResponse = /* @__PURE__ */ (() => Schema.Struct({
  ok: Schema.Literal(true),
  workspaceId: WorkspaceId,
  state: WorkerspaceState
}))();

export const WorkspaceHistoryRestoreResult = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), state: WorkerspaceState }),
  Schema.Struct({ ok: Schema.Literal(false), reason: Schema.Literal("not-found") }),
  Schema.Struct({ ok: Schema.Literal(false), reason: Schema.Literal("conflict"), currentUpdatedAt: Schema.NullOr(Schema.Number) })
]))();
export type WorkspaceHistoryRestoreResult = typeof WorkspaceHistoryRestoreResult.Type;
